import { createSelector } from '@reduxjs/toolkit';

import { RootState } from '../../../../../../init-store';
import { ParticipantState } from '../../../../../pages/call/types';
import { sortParticipants } from '../../../../../shared/helpers';
import { SELF } from '../../../../constants';
import { isSipRoomAgent, selectSyncStateWithPeerId } from '../../../config/selectors';
import { MODAL_TYPES } from '../modal-type/constants';
import { selectModalType } from '../modal-type/selectors';
import { PARTICIPANT_LIST_ITEMS_PER_PAGE } from './constants';

export const selectIsParticipantListOpen = (state: RootState) => {
    return isSipRoomAgent(state) && selectModalType(state) === MODAL_TYPES.participantList;
};

const selectSortedParticipants = createSelector(
    [
        (state: RootState) => state.call.conference.participantsByIds,
        (state: RootState) => selectSyncStateWithPeerId(state),
    ],
    (participantsByIds, syncStateWithPeerId) => {
        if (!participantsByIds.self) {
            return sortParticipants(Object.values(participantsByIds) as ParticipantState[]);
        }

        const participants: ParticipantState[] = [];
        for (const key in participantsByIds) {
            const participant = participantsByIds[key];
            if (key === SELF || !participant) {
                continue;
            }

            if (key === syncStateWithPeerId) {
                participants.push({
                    ...participant,
                    id: SELF,
                });

                continue;
            }

            participants.push(participant);
        }

        return sortParticipants(participants);
    }
);

const selectCurrentPageIdx = (state: RootState) => state.dtmf.participantList.pageIdx;

export const selectParticipantRenderState = createSelector(
    selectSortedParticipants,
    selectCurrentPageIdx,
    (sortedParticipants, currentPageIdx) => {
        const idxStart = currentPageIdx * PARTICIPANT_LIST_ITEMS_PER_PAGE;
        const idxEnd = idxStart + PARTICIPANT_LIST_ITEMS_PER_PAGE;
        const participantListChunk = sortedParticipants.slice(idxStart, idxEnd);
        const totalPages = Math.max(1, Math.ceil(sortedParticipants.length / PARTICIPANT_LIST_ITEMS_PER_PAGE));
        const canGoNextPage = currentPageIdx < totalPages - 1;
        const canGoPrevPage = currentPageIdx > 0;

        return {
            totalParticipants: sortedParticipants.length,
            participantListChunk,
            currentPageIdx,
            totalPages,
            canGoPrevPage,
            canGoNextPage,
        };
    }
);
